
import { ReactNode, useEffect, useState, useRef, useCallback } from 'react';
import { networkService } from '@/services/network.service';
import { syncService } from '@/services/sync.service';
import { offlineDb } from '@/db/offlineDb';
import { toast } from "sonner";
import OfflineLogger from '@/utils/offlineLogger';
import { useAuth } from "@/context/auth/AuthContext";
import { createContext, useContext } from 'react';
import { Badge } from "@/components/ui/badge";
import { getValidUserId } from '@/utils/validation';
import { WifiOff, CloudOff, CloudUpload } from "lucide-react";
import { useThemeConfig } from "@/components/ui/theme-config-provider";
import { safeToastError, safeToastInfo, safeToastSuccess } from '@/components/ui/toastManager';
import { sanitizeErrorMessage } from '@/utils/secureErrorHandler';
import type {
  SyncResetCompletedDetail,
  SyncResetEventDetail,
  SyncResetProgressDetail,
} from '@/services/sync/resetLocalCache';

interface OfflineContextType {
  dbInitialized: boolean;
  isOnline: boolean;
  pendingOperations: number;
  syncNow: () => Promise<void>;
}

const OfflineContext = createContext<OfflineContextType>({
  dbInitialized: false,
  isOnline: true,
  pendingOperations: 0,
  syncNow: async () => {}
});

interface OfflineProviderProps {
  children: ReactNode;
}

export const OfflineProvider = ({ children }: OfflineProviderProps) => {
  const [isOnline, setIsOnline] = useState<boolean>(networkService.isOnline());
  const [pendingOperations, setPendingOperations] = useState<number>(0);
  const [failedOperations, setFailedOperations] = useState<number>(0);
  const [dbInitialized, setDbInitialized] = useState<boolean>(false);
  const { user } = useAuth();
  const lastNetworkStatusRef = useRef<boolean>(networkService.isOnline());
  const toastNotifiedRef = useRef<boolean>(false);
  const reconnectedNotifiedRef = useRef<boolean>(false);
  const { theme } = useThemeConfig();
  
  // Fix 3: Store premium status locally for offline access
  useEffect(() => {
    if (user) {
      // Store user's premium status in localStorage
      localStorage.setItem('userIsPremium', user.isPremium ? 'true' : 'false');
    }
  }, [user?.isPremium]);
  
  // Initialize database
  useEffect(() => {
    let isMounted = true;
    
    const initializeDb = async () => {
      try {
        //OfflineLogger.info('OfflineProvider', 'Initializing offline database');
        
        // Use a try-catch for reopening the database if it's closed
        try {
          if (!offlineDb.isOpen()) {
            await offlineDb.open();
          }
        } catch (error) {
          OfflineLogger.warn('OfflineProvider', `Error checking database state: ${error}`);
          await offlineDb.open();
        }
        
        if (isMounted) {
          setDbInitialized(true);
          //OfflineLogger.info('OfflineProvider', 'Offline database initialized successfully');
        }
        
        // Store userId in localStorage for sync service to use
        if (user?.id) {
          localStorage.setItem('userId', user.id);
        }
      } catch (error) {
        OfflineLogger.error('OfflineProvider', `Error initializing offline database: ${error}`);
        if (isMounted) {
          // If we failed to initialize DB, allow UI to continue but with offline features disabled
          setDbInitialized(false);
        }
      }
    };
    
    initializeDb();
    
    return () => {
      isMounted = false;
      //OfflineLogger.info('OfflineProvider', 'Component unmounted, keeping DB open to avoid async race conditions');
    };
  }, [user?.id]);
  
  // Synchronization event listeners for badge updates
  useEffect(() => {
    // Fix 2: Improved event listeners for sync status changes
    const handleSyncStatusChanged = (e: CustomEvent) => {
      if (e.detail && 'pendingOperations' in e.detail) {
        setPendingOperations(e.detail.pendingOperations);
      }
    };
    
    const handleSyncCompleted = (e: CustomEvent) => {
      // Force check pending operations after sync completes
      if (e.detail) {
        // Directly update with the value if provided
        if ('pendingOperations' in e.detail) {
          setPendingOperations(e.detail.pendingOperations);
        } else {
          // Otherwise check manually
          checkPendingOperations();
        }
      } else {
        checkPendingOperations();
      }
    };
    
    // Add event listeners
    window.addEventListener('syncStatusChanged', handleSyncStatusChanged as EventListener);
    window.addEventListener('syncCompleted', handleSyncCompleted as EventListener);
    
    return () => {
      // Remove event listeners
      window.removeEventListener('syncStatusChanged', handleSyncStatusChanged as EventListener);
      window.removeEventListener('syncCompleted', handleSyncCompleted as EventListener);
    };
  }, []);
  
  // Function to check pending operations
  const checkPendingOperations = useCallback(async () => {
    try {
      if (!offlineDb.isOpen()) {
        // Attempt to reopen the database if it's closed
        try {
          await offlineDb.open();
        } catch (e) {
          OfflineLogger.error('OfflineProvider.checkPendingOperations', `Failed to reopen database: ${e}`);
          return;
        }
      }
      
      const count = await syncService.getPendingOperationsCount();
      setPendingOperations(count);
      const failed = await syncService.getFailedOperationsCount();
      setFailedOperations(failed);
    } catch (error) {
      OfflineLogger.error('OfflineProvider.checkPendingOperations', `Error: ${error}`);
    }
  }, []);
  
  // Effect for monitoring pending operations.
  // I contatori sono aggiornati dagli eventi di sync/reset; qui ricalcoliamo solo
  // all'avvio e quando l'app torna in primo piano, evitando il polling fisso che
  // girava ogni 30s anche ad app inattiva (lavoro CPU/batteria inutile su mobile).
  useEffect(() => {
    if (!dbInitialized) return;

    // Check immediately
    checkPendingOperations();

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        void checkPendingOperations();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [dbInitialized, checkPendingOperations]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const handleResetStarted = (event: Event) => {
      const detail = (event as CustomEvent<SyncResetEventDetail>).detail;
      if (!detail) {
        return;
      }

      void checkPendingOperations();

      if (detail.triggerType === 'remote-command') {
        safeToastInfo("Reset della cache richiesto dall'assistenza, attendi qualche istante...");
      }
    };

    const handleResetProgress = (event: Event) => {
      const detail = (event as CustomEvent<SyncResetProgressDetail>).detail;
      if (!detail) {
        return;
      }

      if (detail.stage === 'cache-cleared') {
        void checkPendingOperations();
      }

      if (detail.stage === 'initial-sync-start' && detail.triggerType === 'remote-command') {
        safeToastInfo('Ricaricamento dati in corso...');
      }
    };

    const handleResetCompleted = (event: Event) => {
      const detail = (event as CustomEvent<SyncResetCompletedDetail>).detail;
      if (!detail) {
        return;
      }

      void checkPendingOperations();

      if (detail.triggerType === 'remote-command') {
        if (detail.reloadedData) {
          safeToastSuccess('Cache locale aggiornata e dati ricaricati');
        } else if (detail.isOnline) {
          const message = detail.initialSyncError
            ? `Cache svuotata ma ricaricamento fallito: ${detail.initialSyncError}`
            : 'Cache svuotata ma ricaricamento dei dati non riuscito. Riprova più tardi.';
          safeToastError(message);
        } else {
          safeToastInfo('Cache locale svuotata. Connettiti a internet per ricaricare i dati.');
        }
      }
    };

    window.addEventListener('syncResetStarted', handleResetStarted as EventListener);
    window.addEventListener('syncResetProgress', handleResetProgress as EventListener);
    window.addEventListener('syncResetCompleted', handleResetCompleted as EventListener);

    return () => {
      window.removeEventListener('syncResetStarted', handleResetStarted as EventListener);
      window.removeEventListener('syncResetProgress', handleResetProgress as EventListener);
      window.removeEventListener('syncResetCompleted', handleResetCompleted as EventListener);
    };
  }, [checkPendingOperations]);

  // Function to manually trigger synchronization
  const syncNow = async () => {
    if (!isOnline) {
      safeToastError("Non puoi sincronizzare quando sei offline");
      return;
    }
    
    try {
      const userId = user?.id || getValidUserId();
      if (userId) {
        safeToastInfo("Sincronizzazione in corso...");
        await syncService.manualSync(userId);
        await checkPendingOperations();
        safeToastSuccess("Sincronizzazione completata", { description: "" });
      } else {
        safeToastError("Impossibile sincronizzare: utente non trovato");
      }
    } catch (error) {
      OfflineLogger.error('OfflineProvider.syncNow', `Error during manual sync: ${error}`);
      safeToastError(`Errore durante la sincronizzazione: ${sanitizeErrorMessage(error)}`);
    }
  };
  
  // Effect for network changes
  useEffect(() => {
    if (!dbInitialized) return;
    
    // Listen for network changes
    const unsubscribe = networkService.addListener(status => {
      setIsOnline(status.connected);
      
      const wasOffline = !lastNetworkStatusRef.current;
      const isNowOnline = status.connected;
      lastNetworkStatusRef.current = status.connected;
      
      if (wasOffline && isNowOnline) {
        // Fix 3: Reset reconnection notification flag for next reconnection
        reconnectedNotifiedRef.current = false;
        
        // Only check pending operations when transitioning from offline to online
        syncService.getPendingOperationsCount().then(count => {
          setPendingOperations(count);
          
          // Only show a toast notification if there are pending operations
          // and we haven't already shown one for this session
          if (count > 0 && !toastNotifiedRef.current && !reconnectedNotifiedRef.current) {
            safeToastSuccess(`Sei tornato online! ${count} operazioni da sincronizzare.`, { description: "" });
            toastNotifiedRef.current = true;
            reconnectedNotifiedRef.current = true;
            
            // Start synchronization with userId
            const userId = user?.id || getValidUserId();
            if (userId) {
              syncService.syncData(userId);
            }
          } else if (count === 0) {
            // Reset the notification flag when there's nothing to sync
            toastNotifiedRef.current = false;
          }
        }).catch(err => {
          OfflineLogger.error('OfflineProvider.networkChange', `Error checking pending operations: ${err}`);
        });
      } else if (!isNowOnline && !wasOffline) {
        // Going offline - reset notification flag for next time we're online
        toastNotifiedRef.current = false;
        
        // Show offline notification only once
        safeToastInfo("Sei offline. Le modifiche verranno sincronizzate quando tornerai online.");
        //OfflineLogger.info('OfflineProvider', 'Network disconnected, switching to offline mode');
      }
    });
    
    return () => {
      unsubscribe();
    };
  }, [dbInitialized, user?.id]);
  
  return (
    <OfflineContext.Provider value={{ dbInitialized, isOnline, pendingOperations, syncNow }}>
      {children}
      
      {/* Fixed 2: Floating network status badge - only show when there's something to show */}
      {(!isOnline || pendingOperations > 0 || failedOperations > 0) && (
        <div
          className="fixed left-4 z-50 flex flex-col gap-2 items-start"
          style={{ bottom: "calc(1rem + 20px + var(--safe-area-bottom, 0px))" }}
        >
          {!isOnline && (
            <Badge
              variant="outline"
              className="py-2 px-3 animate-pulse"
              style={{
                backgroundColor: `${theme.errorColor}20`,
                color: theme.errorColor,
                borderColor: theme.errorColor,
              }}
            >
              <WifiOff className="w-4 h-4 mr-1" />
              <span>Offline</span>
            </Badge>
          )}

          {pendingOperations > 0 && (
            <button
              onClick={syncNow}
              disabled={!isOnline}
              className="py-2 px-3 flex items-center gap-1 rounded-full disabled:opacity-50"
              style={{
                backgroundColor: `${theme.warningColor}20`,
                color: theme.warningColor,
                border: `1px solid ${theme.warningColor}`,
              }}
            >
              <CloudUpload className="w-4 h-4 mr-1" />
              <span>
                {pendingOperations} {pendingOperations === 1 ? 'modifica' : 'modifiche'} da sincronizzare
              </span>
            </button>
          )}

          {failedOperations > 0 && (
            <button
              onClick={syncNow}
              disabled={!isOnline}
              aria-label={`${failedOperations} ${failedOperations === 1 ? 'modifica non sincronizzata' : 'modifiche non sincronizzate'}, tocca per riprovare`}
              className="py-2 px-3 flex items-center gap-1 rounded-full disabled:opacity-50"
              style={{
                backgroundColor: `${theme.errorColor}20`,
                color: theme.errorColor,
                border: `1px solid ${theme.errorColor}`,
              }}
            >
              <CloudOff className="w-4 h-4 mr-1" />
              <span>
                {failedOperations} {failedOperations === 1 ? 'non sincronizzata' : 'non sincronizzate'} · tocca per riprovare
              </span>
            </button>
          )}
        </div>
      )}
    </OfflineContext.Provider>
  );
};

export default OfflineProvider;
export const useOfflineStatus = () => useContext(OfflineContext);
